import React, { useState } from 'react';
import { supabase } from '../supabase';

// view: 'signin' | 'signup' | 'forgot' | 'forgot-sent' | 'leaderboard-preview'
export function AuthPage({ onAuthSuccess, title = '🩷Flutter Heart' }) {
  const [view, setView] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const reset = (nextView) => { setError(''); setSuccessMessage(''); setShowPassword(false); setView(nextView); };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        setError(error.message);
      } else if (data?.user) {
        onAuthSuccess(data.user);
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({ email, password });
      if (error) {
        setError(error.message);
      } else if (data?.user?.identities?.length === 0) {
        setError('That email is already registered. Please sign in.');
        reset('signin');
      } else if (data?.user) {
        onAuthSuccess(data.user);
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin,
      });
      if (error) {
        setError(error.message);
      } else {
        setView('forgot-sent');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-box">
        <h1 className="auth-title">{title}</h1>

        {view === 'signin' && (
          <form onSubmit={handleSignIn}>
            <h2>Sign in</h2>
            <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
            <div className="password-field">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button type="button" className="show-hide-btn" onClick={() => setShowPassword(v => !v)}>
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            <button type="submit" disabled={loading}>{loading ? 'Signing in…' : 'Sign In'}</button>
            <button type="button" className="link-btn" onClick={() => reset('signup')}>Don't have an account? Sign Up</button>
            <button type="button" className="link-btn" onClick={() => reset('forgot')}>Forgot password?</button>
          </form>
        )}

        {view === 'signup' && (
          <form onSubmit={handleSignUp}>
            <h2>Create account</h2>
            <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
            <div className="password-field">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password (min 6 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
              <button type="button" className="show-hide-btn" onClick={() => setShowPassword(v => !v)}>
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            <button type="submit" disabled={loading}>{loading ? 'Creating…' : 'Sign Up'}</button>
            <button type="button" className="link-btn" onClick={() => reset('signin')}>Already have an account? Sign In</button>
          </form>
        )}

        {view === 'forgot' && (
          <form onSubmit={handleForgotPassword}>
            <h2>Reset password</h2>
            <p style={{ fontSize: 13, color: '#a05070', marginBottom: 14 }}>Enter your email and we'll send you a reset link.</p>
            <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
            <button type="submit" disabled={loading}>{loading ? 'Sending…' : 'Send Reset Link'}</button>
            <button type="button" className="link-btn" onClick={() => reset('signin')}>Back to Sign In</button>
          </form>
        )}

        {view === 'forgot-sent' && (
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <p style={{ fontSize: 28, marginBottom: 12 }}>📬</p>
            <p style={{ fontWeight: 600, marginBottom: 8, color: '#7e2255' }}>Check your email</p>
            <p style={{ fontSize: 13, color: '#a05070', marginBottom: 20 }}>We sent a password reset link to <strong>{email}</strong>.</p>
            <button type="button" className="link-btn" onClick={() => reset('signin')}>Back to Sign In</button>
          </div>
        )}

        {error && <div className="error">{error}</div>}
        {successMessage && <div className="success">{successMessage}</div>}
      </div>
    </div>
  );
}